///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Saved Views list for the Views menu (top-right, beside the bookmark
/// button): search, sort (Recent / A-Z / Nearby), thumbnail rows with
/// copy-link, embed and two-step delete. Clicking a row flies to it.
///

import { ViewThumbnailStore } from "@/earthbrowser/camera-director/ViewThumbnailStore"
import { useViewThumbnail } from "@/hooks/useViewThumbnail"
import { setSavedViewsSortMode, type DialogState } from "@/redux/dialogSlice"
import { sendCameraCommand } from "@/redux/earthBrowserSlice"
import { deleteView, type SavedView } from "@/redux/savedViewsSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useShareLink } from "@/hooks/useShareLink"
import { viewEmbedSnippet } from "@/utils/embedSnippet"
import { cEmbedCodeEnabled } from "@/utils/featureGates"
import { cameraLatLonDeg, haversineKm, relativeTime } from "@/utils/savedViews"
import { cn } from "@/utils/utils"
import { ArrowDownAZ, Check, Clock, CodeXml, Link, MapPin, Navigation, Search, Trash2 } from "lucide-react"
import { useCallback, useMemo, useState } from "react"

type SortMode = DialogState["savedViewsSortMode"]

const cSortOptions: { mode: SortMode; label: string; icon: React.ReactNode }[] = [
	{ mode: "recent", label: "Recent", icon: <Clock className="w-3 h-3" /> },
	{ mode: "alpha", label: "A–Z", icon: <ArrowDownAZ className="w-3 h-3" /> },
	{ mode: "nearby", label: "Nearby", icon: <Navigation className="w-3 h-3" /> },
]

function distanceLabel(km: number): string {
	if (km < 1) return "here"
	if (km < 100) return `${Math.round(km)} km away`
	return `${Math.round(km / 100) * 100} km away`
}

interface ViewRowProps {
	view: SavedView
	subtitle: string
	onFlyTo: (viewId: string) => void
}

function ViewRow({ view, subtitle, onFlyTo }: ViewRowProps) {
	const dispatch = useAppDispatch()
	const thumbnailUrl = useViewThumbnail(view.id)
	const { shareView } = useShareLink()
	const [linkCopied, setLinkCopied] = useState(false)
	const [embedCopied, setEmbedCopied] = useState(false)
	const [confirmingDelete, setConfirmingDelete] = useState(false)

	// Signed in: server-backed short URL (?s=); signed out: param deep link
	const handleCopyLink = (e: React.MouseEvent) => {
		e.stopPropagation()
		void shareView(view).then(url => navigator.clipboard.writeText(url)).then(() => {
			setLinkCopied(true)
			setTimeout(() => setLinkCopied(false), 1500)
		})
	}

	// Copy-paste iframe embed snippet (self-contained; works signed out)
	const handleCopyEmbed = (e: React.MouseEvent) => {
		e.stopPropagation()
		void navigator.clipboard.writeText(viewEmbedSnippet(view)).then(() => {
			setEmbedCopied(true)
			setTimeout(() => setEmbedCopied(false), 1500)
		})
	}

	// Two-step delete: first click arms (icon turns red), second deletes.
	// Arming resets when the pointer leaves the row.
	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!confirmingDelete) {
			setConfirmingDelete(true)
			return
		}
		dispatch(deleteView(view.id))
		void ViewThumbnailStore.shared.deleteThumbnail(view.id)
	}

	// Div instead of button so the row can host the action buttons
	return (
		<div
			onClick={() => onFlyTo(view.id)}
			onMouseLeave={() => setConfirmingDelete(false)}
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					onFlyTo(view.id)
				}
			}}
			className={cn(
				"group/row flex items-center gap-2.5 w-full px-2 py-1.5 rounded-lg text-left cursor-pointer",
				"hover:bg-white/10 active:bg-white/20 transition-colors duration-200",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
			)}
			title={`Fly to "${view.name}"`}
		>
			<div className="relative w-12 h-8 rounded-md overflow-hidden bg-slate-900/60 flex-shrink-0">
				{thumbnailUrl ? (
					<img src={thumbnailUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
				) : (
					<div className="absolute inset-0 flex items-center justify-center text-white/25">
						<MapPin className="w-3.5 h-3.5" />
					</div>
				)}
			</div>
			<div className="flex-1 min-w-0">
				<div className="text-sm text-white/85 truncate">{view.name}</div>
				<div className="text-[10px] text-white/40 truncate">{subtitle}</div>
			</div>
			<div className="flex items-center gap-0.5 flex-shrink-0">
				<button
					onClick={handleCopyLink}
					className={cn(
						"w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-200",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
						linkCopied
							? "text-emerald-400"
							: "text-white/35 hover:text-white hover:bg-white/10"
					)}
					title="Copy link to this view"
					aria-label={`Copy link to ${view.name}`}
				>
					{linkCopied ? <Check className="w-3.5 h-3.5" /> : <Link className="w-3.5 h-3.5" />}
				</button>
				{cEmbedCodeEnabled && (
					<button
						onClick={handleCopyEmbed}
						className={cn(
							"w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-200",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
							embedCopied
								? "text-emerald-400"
								: "text-white/35 hover:text-white hover:bg-white/10"
						)}
						title="Copy embed code for this view"
						aria-label={`Copy embed code for ${view.name}`}
					>
						{embedCopied ? <Check className="w-3.5 h-3.5" /> : <CodeXml className="w-3.5 h-3.5" />}
					</button>
				)}
				<button
					onClick={handleDelete}
					className={cn(
						"w-6 h-6 rounded-md flex items-center justify-center transition-colors duration-200",
						"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
						confirmingDelete
							? "text-red-400 bg-red-500/20 hover:bg-red-500/30"
							: "text-white/35 hover:text-red-400 hover:bg-white/10"
					)}
					title={confirmingDelete ? "Click again to delete" : "Delete view"}
					aria-label={confirmingDelete ? `Confirm delete ${view.name}` : `Delete ${view.name}`}
				>
					<Trash2 className="w-3.5 h-3.5" />
				</button>
			</div>
		</div>
	)
}

interface SavedViewsListProps {
	/** Close the hosting menu after an action */
	onDone: () => void
}

/** Search + sort + rows */
export function SavedViewsList({ onDone }: SavedViewsListProps) {
	const dispatch = useAppDispatch()
	const views = useAppSelector(state => state.savedViews.views)
	const sortMode = useAppSelector(state => state.dialog.savedViewsSortMode)
	const cameraState = useAppSelector(state => state.earthbrowser.cameraState)
	const [search, setSearch] = useState("")

	const sorted = useMemo(() => {
		const query = search.trim().toLowerCase()
		const list = Object.values(views).filter(view =>
			!query ||
			view.name.toLowerCase().includes(query) ||
			view.tags.some(tag => tag.toLowerCase().includes(query))
		)

		if (sortMode === "alpha") {
			return list
				.sort((a, b) => a.name.localeCompare(b.name))
				.map(view => ({ view, subtitle: relativeTime(view.updatedAt) }))
		}

		if (sortMode === "nearby") {
			const here = cameraLatLonDeg(cameraState.theta, cameraState.phi)
			return list
				.map(view => {
					const there = cameraLatLonDeg(view.camera.theta, view.camera.phi)
					return { view, km: haversineKm(here.lat, here.lon, there.lat, there.lon) }
				})
				.sort((a, b) => a.km - b.km)
				.map(({ view, km }) => ({ view, subtitle: distanceLabel(km) }))
		}

		return list
			.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
			.map(view => ({ view, subtitle: relativeTime(view.updatedAt) }))
	}, [views, search, sortMode, cameraState.theta, cameraState.phi])

	const handleFlyTo = useCallback((viewId: string) => {
		dispatch(sendCameraCommand({ command: "view/flyTo", data: { viewId } }))
		onDone()
	}, [dispatch, onDone])

	const empty = Object.keys(views).length === 0

	return (
		<div className="flex flex-col gap-1.5 min-h-0">
			<div className="relative flex-shrink-0">
				<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
				<input
					value={search}
					onChange={e => setSearch(e.target.value)}
					placeholder="Search views…"
					className={cn(
						"w-full pl-8 pr-2 py-1.5 text-sm rounded-lg",
						"bg-white/5 text-white border border-white/10",
						"focus:outline-none focus:border-cyan-500/50 placeholder:text-white/40"
					)}
				/>
			</div>

			<div className="flex gap-1 flex-shrink-0" role="radiogroup" aria-label="Sort saved views">
				{cSortOptions.map(option => (
					<button
						key={option.mode}
						onClick={() => dispatch(setSavedViewsSortMode(option.mode))}
						role="radio"
						aria-checked={sortMode === option.mode}
						className={cn(
							"flex-1 flex items-center justify-center gap-1 px-2 py-1 text-[11px] rounded-full",
							"transition-colors duration-200",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
							sortMode === option.mode
								? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/70"
								: "bg-white/5 text-white/50 border border-transparent hover:text-white hover:bg-white/10"
						)}
					>
						{option.icon}
						{option.label}
					</button>
				))}
			</div>

			<div className="flex-1 min-h-0 overflow-y-auto">
				{sorted.length > 0 ? (
					sorted.map(({ view, subtitle }) => (
						<ViewRow key={view.id} view={view} subtitle={subtitle} onFlyTo={handleFlyTo} />
					))
				) : (
					<p className="px-2 py-3 text-xs text-white/40 text-center">
						{empty ? "No saved views yet — press ⌘D to save one" : "No views match your search"}
					</p>
				)}
			</div>
		</div>
	)
}
